import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listBillings, createBilling, updateBilling, deleteBilling, listProjects, updateServiceRequest } from '../lib/adminApi';
import { COUNTRIES } from '../data/countries';
import ConfirmDialog from '../components/ConfirmDialog';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'SGD', 'AUD', 'CAD', 'JPY', 'CNY', 'INR', 'MYR', 'HKD', 'AED'];

const EMPTY_FORM = {
  customer_name: '',
  customer_organisation: '',
  customer_email: '',
  customer_country: '',
  customer_mobile: '',
  billing_amount: '',
  billing_currency: 'SGD',
  payment_status: 'unpaid',
  amount_paid: '',
};

function outstandingOf(form) {
  const amount = Number(form.billing_amount) || 0;
  if (form.payment_status === 'paid') return 0;
  if (form.payment_status === 'partial') return Math.max(0, amount - (Number(form.amount_paid) || 0));
  return amount;
}

function formatMoney(currency, value) {
  return `${currency} ${Number(value).toFixed(2)}`;
}

function PaymentFields({ form, onChange, disabled }) {
  return (
    <>
      <div className="admin-form-row">
        <div className="admin-field">
          <label>Payment Receipt</label>
          <select disabled={disabled} value={form.payment_status} onChange={onChange('payment_status')} required>
            <option value="">Select…</option>
            <option value="unpaid">No</option>
            <option value="partial">Partial</option>
            <option value="paid">Yes</option>
          </select>
        </div>
        <div className="admin-field">
          <label>Outstanding Payment</label>
          <input disabled value={formatMoney(form.billing_currency, outstandingOf(form))} />
        </div>
      </div>
      {form.payment_status === 'partial' && (
        <div className="admin-field">
          <label>Amount Paid</label>
          <input
            type="number" min="0" step="0.01" disabled={disabled}
            value={form.amount_paid}
            onChange={onChange('amount_paid')}
          />
        </div>
      )}
    </>
  );
}

export default function BillingsCustomers() {
  const [view, setView] = useState('list'); // 'list' | 'form' | 'detail'
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [emailStatusMap, setEmailStatusMap] = useState({}); // billingId -> {status, error, sentAt}
  const location = useLocation();
  const navigate = useNavigate();
  const wizard = Boolean(location.state?.wizard);
  const pendingRequest = location.state?.pendingRequest || null;
  const existingBillingId = location.state?.billingId || null;

  const load = () => {
    setLoading(true);
    listBillings()
      .then((data) => setBillings(data.billings || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const loadEmailStatus = async (billingId) => {
    if (emailStatusMap[billingId]) return;
    try {
      const data = await listProjects();
      const projects = (data.projects || []).filter(p => p.billing_id === billingId);
      if (projects.length > 0) {
        const latestProject = projects.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        setEmailStatusMap(prev => ({
          ...prev,
          [billingId]: {
            status: latestProject.email_status || latestProject.emailStatus || 'unknown',
            error: latestProject.email_error || latestProject.emailError || null,
            sentAt: latestProject.email_sent_at || latestProject.created_at,
          }
        }));
      }
    } catch (err) {
      console.error('Failed to load email status:', err);
    }
  };

  useEffect(() => {
    load();
    if (wizard) {
      setView('form');
      if (pendingRequest && !existingBillingId) {
        setForm({
          ...EMPTY_FORM,
          customer_name: pendingRequest.customer_name || pendingRequest.customer_name || '',
          customer_organisation: pendingRequest.customer_organisation || pendingRequest.customer_organisation || '',
          customer_email: pendingRequest.customer_email || pendingRequest.customer_email || '',
          customer_country: pendingRequest.customer_country || pendingRequest.customer_country || '',
          customer_mobile: pendingRequest.customer_mobile || pendingRequest.customer_mobile || '',
          billing_amount: pendingRequest.billing_amount ?? pendingRequest.billing_amount ?? '',
          billing_currency: pendingRequest.billing_currency || 'SGD',
          payment_status: '',
          amount_paid: '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dialCode = COUNTRIES.find((c) => c.name === form.customer_country)?.dialCode || '';

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let saved;
      if (existingBillingId) {
        saved = await updateBilling(existingBillingId, { ...form, customer_country_code: dialCode });
      } else {
        saved = await createBilling({ ...form, customer_country_code: dialCode });
      }
      const billing = saved.billing;
      if (pendingRequest?.id) {
        await updateServiceRequest(pendingRequest.id, { billing_id: billing.id });
      }
      setForm(EMPTY_FORM);
      if (wizard) {
        navigate('/admin/projects', { state: { wizard: true, billingId: billing.id, pendingRequest } });
        return;
      }
      setView('list');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (billing) => {
    setSelectedId(billing.id);
    setEditing(false);
    setError('');
    setForm({
      customer_name: billing.customer_name || '',
      customer_organisation: billing.customer_organisation || '',
      customer_email: billing.customer_email || '',
      customer_country: billing.customer_country || '',
      customer_mobile: billing.customer_mobile || '',
      billing_amount: billing.billing_amount,
      billing_currency: billing.billing_currency,
      payment_status: billing.payment_status || 'unpaid',
      amount_paid: billing.amount_paid || '',
    });
    loadEmailStatus(billing.id);
    setView('detail');
  };

  const handleSaveEdits = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await updateBilling(selectedId, { ...form, customer_country_code: dialCode });
      setEditing(false);
      setView('list');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteBilling(target.id);
      setBillings((prev) => prev.filter((b) => b.id !== target.id));
    } catch (err) {
      setError(err.message);
    }
  };

  if (view === 'form' || view === 'detail') {
    const isDetail = view === 'detail';
    const readOnly = isDetail && !editing;

    return (
      <div>
        {!wizard && (
          <button
            className="admin-back-link"
            onClick={() => { setView('list'); setEditing(false); }}
          >
            Back to Billings and Customers Homepage
          </button>
        )}
        <div className="admin-page-header">
          <div>
            <h1>{isDetail ? form.customer_name : (wizard ? 'Create New Service — Step 1 of 2' : 'New Billing & Customer Record')}</h1>
            <p>Billing amount and currency, plus the customer's contact details.</p>
          </div>
          {isDetail && (
            <button
              className="admin-btn admin-btn-primary"
              onClick={(e) => (editing ? handleSaveEdits(e) : setEditing(true))}
              disabled={saving}
            >
              {editing ? (saving ? 'Saving…' : 'Save Edits') : 'Edit'}
            </button>
          )}
        </div>

        {wizard && (
          <div className="admin-steps">
            <div className="admin-step active">1. Billings and Customer</div>
            <div className="admin-step">2. Project</div>
          </div>
        )}

        <div className="admin-card">
          {error && <div className="admin-alert admin-alert-error">{error}</div>}
          <form className="admin-form" onSubmit={isDetail ? handleSaveEdits : handleSave}>
            <div className="admin-form-row">
              <div className="admin-field">
                <label>Customer Name *</label>
                <input required disabled={readOnly} value={form.customer_name} onChange={handleChange('customer_name')} />
              </div>
              <div className="admin-field">
                <label>Organisation (optional)</label>
                <input disabled={readOnly} value={form.customer_organisation} onChange={handleChange('customer_organisation')} />
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-field">
                <label>Email *</label>
                <input type="email" required disabled={readOnly} value={form.customer_email} onChange={handleChange('customer_email')} />
                <span className="admin-hint">The final canvas is emailed here when the service ends.</span>
                {emailStatusMap[selectedId] && (
                  <div className={`admin-email-status admin-email-status-${emailStatusMap[selectedId].status}`}>
                    {emailStatusMap[selectedId].status === 'sent' && (
                      <>
                        The weblink and activation code has been successfully sent on{' '}
                        <strong>{new Date(emailStatusMap[selectedId].sentAt).toLocaleString()}</strong>.
                        <br />The final canvas is emailed here when the service ends.
                      </>
                    )}
                    {emailStatusMap[selectedId].status === 'failed' && (
                      <>
                        The weblink and activation code could not be sent due to{' '}
                        <strong>{emailStatusMap[selectedId].error || 'unknown error'}</strong>.
                        <br />Please provide a valid email address. The final canvas will be emailed to this address when the service ends.
                      </>
                    )}
                    {emailStatusMap[selectedId].status === 'no_email' && (
                      <>
                        No email address provided. The weblink and activation code could not be sent.
                        <br />Please provide a valid email address. The final canvas will be emailed to this address when the service ends.
                      </>
                    )}
                    {emailStatusMap[selectedId].status === 'not_sent' && (
                      <>
                        The weblink and activation code has not been sent yet.
                        <br />The final canvas will be emailed here when the service ends.
                      </>
                    )}
                    {emailStatusMap[selectedId].status === 'unknown' && (
                      <>
                        Email status unknown. The final canvas will be emailed here when the service ends.
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="admin-field">
                <label>Country *</label>
                <select required disabled={readOnly} value={form.customer_country} onChange={handleChange('customer_country')}>
                  <option value="">Select a country…</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.iso2} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-field">
                <label>Mobile Number (Optional)</label>
                <div className="admin-input-group">
                  <span className="admin-prefix">{dialCode || '—'}</span>
                  <input disabled={readOnly} value={form.customer_mobile} onChange={handleChange('customer_mobile')} placeholder="912 345 678" />
                </div>
                <span className="admin-hint">Country code is set automatically from the selected country.</span>
              </div>
              <div className="admin-field">
                <label>Billing Amount *</label>
                <div className="admin-input-group">
                  <select
                    disabled={readOnly}
                    value={form.billing_currency}
                    onChange={handleChange('billing_currency')}
                    style={{ maxWidth: 100 }}
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    type="number" min="0" step="0.01" required disabled={readOnly}
                    value={form.billing_amount}
                    onChange={handleChange('billing_amount')}
                  />
                </div>
              </div>
            </div>

            <PaymentFields form={form} onChange={handleChange} disabled={readOnly} />

            {!isDetail && (
              <div className="admin-form-actions">
                {!wizard && (
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setView('list')}>
                    Cancel
                  </button>
                )}
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : wizard ? 'Save & Continue to Projects →' : 'Save'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1>Billings and Customers</h1>
          <p>Billing amount, currency and customer contact details.</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => { setForm(EMPTY_FORM); setView('form'); }}>+ New Record</button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Organisation</th>
              <th>Email</th>
              <th>Country</th>
              <th>Billing</th>
              <th>Payment Receipt</th>
              <th>Outstanding</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {billings.map((b) => (
              <tr key={b.id}>
                <td className="clickable" onClick={() => openDetail(b)}>{b.customer_name}</td>
                <td onClick={() => openDetail(b)}>{b.customer_organisation || '—'}</td>
                <td onClick={() => openDetail(b)}>{b.customer_email}</td>
                <td onClick={() => openDetail(b)}>{b.customer_country}</td>
                <td onClick={() => openDetail(b)}>{formatMoney(b.billing_currency, b.billing_amount)}</td>
                <td onClick={() => openDetail(b)}>{{ paid: 'Yes', partial: 'Partial', unpaid: 'No' }[b.payment_status] || 'No'}</td>
                <td onClick={() => openDetail(b)}>{formatMoney(b.billing_currency, outstandingOf(b))}</td>
                <td>
                  {b.project_count === 0 && (
                    <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteTarget(b)}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && billings.length === 0 && (
          <div className="admin-empty">No billing/customer records yet.</div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete customer?"
        message="Are you sure you want to delete customer?"
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
